import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { columns } from './HazardSurveys.columns';
import { GET_JOB_IDS, GET_JOBS, JobItem } from './HazardSurveys.query';
import { RouteComponentProps, withRouter } from 'react-router';
import FilterBarHazardSurvey, {
  defaultFilters,
  JobFilters,
  stringSearchFields,
} from './FilterBarHazardSurvey';
import useStoredFilters from '../../../../hooks/useStoredFilters';
import { useOrderBy } from '../../../../hooks/useOrderBy';
import useMode from '../../../../hooks/useMode';
import { UserMode } from '../../../../hooks/useApolloClient';
import useTablePages from '../../../../hooks/useTablePages';
import useColumnConfiguration from '../../../../hooks/useColumnConfiguration';
import useThirdPaneCallbacks from '../../../../hooks/useThirdPaneCallbacks';
import ThirdPaneLayout, {
  MainColumn,
} from '../../../../components/Layout/ThirdPaneLayout';
import PageBar from '../../../../components/PageBar/PageBar';
import Button from '../../../../components/Button/Button';
import TableWindow from '../../../../components/Table/TableWindow';
import JobsActions from './JobsActions';
import HazardSurveyThirdPane from '../../../../components/WaterHazard/HazardSurveyThirdPane';
import { getStringSearchOr } from '../../../../helpers/search';
import { Link } from 'react-router-dom';

const convertStoredString = (searchString: string) => ({
  or: getStringSearchOr(stringSearchFields, searchString),
});

const convertStoredDate = (
  dateStart: string | undefined,
  dateEnd: string | undefined,
  currentDateFilterField = 'job.backflowTest.dateTested'
) => {
  return {
    [currentDateFilterField]:
      dateStart && dateEnd
        ? {
            between: [dateStart, dateEnd],
          }
        : dateStart || dateEnd
          ? {
              gte: dateStart ? dateStart : undefined,
              lte: dateEnd ? dateEnd + 'T23:59:59.999Z' : undefined,
            }
          : undefined,
  };
};

export const convertFilters = (storedFilters: JobFilters) => {
  const where: {
    'waterHazardSurvey.status'?: string[];
    and: Record<string, any>[];
  } = {
    and: [],
  };
  if (storedFilters.status) {
    where['waterHazardSurvey.status'] = storedFilters.status;
  }
  if (storedFilters.stringSearch) {
    where.and.push(convertStoredString(storedFilters.stringSearch));
  }
  if (
    storedFilters.currentDateFilterField !== 'ALL' &&
    (storedFilters.dateStart || storedFilters.dateEnd)
  ) {
    where.and.push(
      convertStoredDate(
        storedFilters.dateStart,
        storedFilters.dateEnd,
        storedFilters.currentDateFilterField
      )
    );
  }

  if (where.and.length === 0) {
    delete (where as any).and;
  }

  return where;
};

type JobDetailParams = {
  id?: string;
};

type JobDetailProps = RouteComponentProps<JobDetailParams>;

/**
 * @component for Hazard Surveys page
 */
const HazardSurveys: React.FC<JobDetailProps> = ({ match }) => {
  const { storedFilters, setStoredFilters, queryFilters } =
    useStoredFilters<JobFilters>(
      convertFilters,
      'waterSupplierHazardSurveys',
      defaultFilters
    );

  const { orderBy, onHeaderClick } = useOrderBy(
    'waterSupplierHazardSurveys',
    columns
  );

  const mode: UserMode = useMode();

  const { loading, error, itemIds, itemLoader } = useTablePages<JobItem>(
    'jobs',
    GET_JOB_IDS as any,
    {
      where: {
        ...queryFilters,
        type: 8,
      },
      limit: null,
      orderBy,
    },
    GET_JOBS as any,
    (ids) => ({
      where: {
        id: ids,
        type: 8,
      },
      limit: null,
    }),
    'id'
  );

  const [selectedIds, setSelectedIds] = useState([] as readonly string[]);

  const { visibleColumns, columnConfiguration, setColumnConfiguration } =
    useColumnConfiguration(columns, 'storedColumnConfigurationJobs');

  const { closeThirdPane, onRowClick, prev, prevEnabled, next, nextEnabled } =
    useThirdPaneCallbacks(
      `/${mode}/hazard-surveys`,
      itemIds,
      match.params.id,
      'id'
    );

  return (
    <ThirdPaneLayout paneVisible={!!match.params.id}>
      <MainColumn px="mainContent" pb="mainContent">
        <PageBar title="Hazard Surveys">
          <Button icon="plus" as={Link} to={`/${mode}/hazard-surveys/new`}>
            New Hazard Survey
          </Button>
        </PageBar>
        <FilterBarHazardSurvey
          mb={4}
          filters={storedFilters}
          setFilters={setStoredFilters}
          selectedId={match.params.id}
        />
        {loading && itemIds.length === 0 ? (
          'Loading...'
        ) : (
          <TableWindow
            emptyMessage="No hazard surveys found."
            columns={visibleColumns}
            itemIds={itemIds}
            itemLoader={itemLoader}
            onSelectionChange={setSelectedIds}
            selectedIds={selectedIds}
            actionItems={<JobsActions assignmentIds={selectedIds} />}
            activeIds={match.params.id ? [match.params.id] : undefined}
            onHeaderClick={onHeaderClick}
            orderBy={orderBy}
            onRowClick={onRowClick}
            columnConfiguration={columnConfiguration}
            setColumnConfiguration={setColumnConfiguration}
          />
        )}
      </MainColumn>
      <HazardSurveyThirdPane
        jobId={match.params.id}
        onPrev={prev}
        prevEnabled={prevEnabled}
        onNext={next}
        nextEnabled={nextEnabled}
        onToggle={closeThirdPane}
      />
    </ThirdPaneLayout>
  );
};

HazardSurveys.propTypes = {
  match: PropTypes.any,
};

export default withRouter(HazardSurveys);
